import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { makeAuthenticatedRequest } from '../services/api';
import { FaArrowLeft, FaCloudDownloadAlt, FaSpinner, FaUsers, FaChevronLeft, FaChevronRight, FaExclamationCircle } from 'react-icons/fa';
import { Button } from '../components/forms';
import useDemoPermissions from '../hooks/useDemoPermissions';

const PAGE_SIZE = 50;

const ImportXeroContactsPage = () => {
  const { isDemo } = useDemoPermissions();
  const navigate = useNavigate();
  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // For display purposes
  const [pageDocIds, setPageDocIds] = useState([null]); // Store doc ID for start of each page, null for page 1

  const fetchContacts = useCallback(async (startAfterDocId = null, pageNum = 1) => {
    setIsLoadingContacts(true);
    setContactsError(null);
    try {
      const requestBody = {
        pageSize: PAGE_SIZE,
      };
      if (startAfterDocId) {
        requestBody.startAfterDocId = startAfterDocId;
      }
      
      const apiResponse = await makeAuthenticatedRequest('/xero/contacts', {
        method: 'POST',
        body: requestBody,
      });

      if (apiResponse && Array.isArray(apiResponse.contacts)) {
        setContacts(apiResponse.contacts);
        setNextPageDocId(apiResponse.nextPageDocId || null);
        setCurrentPage(pageNum);

        if (pageNum >= pageDocIds.length && apiResponse.nextPageDocId) {
          setPageDocIds(prev => [...prev, apiResponse.nextPageDocId]);
        } else if (pageNum < pageDocIds.length && !apiResponse.nextPageDocId) {
          setPageDocIds(prev => prev.slice(0, pageNum));
        }
      } else {
        console.error('Error: Unexpected response structure from /xero/contacts API:', apiResponse);
        throw new Error('Failed to process contacts from Xero. Unexpected data format received.');
      }
    } catch (error) {
      console.error('Error fetching Xero contacts:', error);
      setContactsError(error.message || 'An unknown error occurred while fetching contacts.');
      setContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [pageDocIds]);

  useEffect(() => {
    fetchContacts(null, 1); // Fetch initial page on component mount
  }, [fetchContacts]);

  const handleImportContacts = async () => {
    setIsLoadingImport(true);
    setImportResult(null);
    setImportError(null);
    try {
      const response = await makeAuthenticatedRequest('/xero/stage-contacts', {
        method: 'POST',
        body: {}, // Empty body as requested
      });
      setImportResult(response);
    } catch (error) {
      console.error('Error importing Xero contacts:', error);
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      setImportError(message);
    } finally {
      setIsLoadingImport(false);
    }
  };

  const handleNextPage = () => {
    if (nextPageDocId) {
      fetchContacts(nextPageDocId, currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPageStartDocId = pageDocIds[currentPage - 2];
      fetchContacts(prevPageStartDocId, currentPage - 1);
    }
  };
  
  const handleContactClick = (contactId) => {
    navigate(`/settings/xero-contact-details/${contactId}`);
  };

  if (isDemo) return <Navigate to="/" replace />;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FaUsers className="mr-3" /> Xero Contacts
        </h1>
        <Link to="/settings" className="text-gray-900 hover:text-green-600 underline flex items-center">
          <FaArrowLeft className="mr-1" /> Back to Settings
        </Link>
      </div>

      {/* Section for Staging Contacts (existing functionality) */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-6 mb-8">
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Stage Contacts from Xero</h2>
          <p className="text-sm text-gray-600 mb-4">
            Click the button below to fetch contacts from Xero and stage them in the system.
            This will make an API call to <code>POST /api/xero/stage-contacts</code> with an empty request body.
          </p>
          <Button onClick={handleImportContacts} disabled={isLoadingImport} variant="primary" loading={isLoadingImport}>
            {!isLoadingImport && <FaCloudDownloadAlt className="mr-2" />}
            {isLoadingImport ? 'Importing...' : 'Import Now'}
          </Button>
        </div>
        {importError && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
            <p className="font-semibold">Error staging contacts:</p>
            <p>{importError}</p>
          </div>
        )}
        {importResult && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Staging Result:</h3>
            <div className="bg-gray-50 p-4 rounded border border-gray-200 max-h-96 overflow-auto">
              <pre className="text-sm whitespace-pre-wrap break-all">
                {JSON.stringify(importResult, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Section for Displaying Contacts */}
      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Xero Contacts</h2>
        
        {isLoadingContacts && (
          <div className="flex justify-center items-center py-10">
            <FaSpinner className="animate-spin text-3xl text-green-600" />
            <p className="ml-3 text-gray-600">Loading contacts...</p>
          </div>
        )}

        {contactsError && (
          <div className="my-4 p-4 bg-red-100 text-red-700 border border-red-300 rounded text-center">
            <FaExclamationCircle className="text-3xl text-red-500 mx-auto mb-2" />
            <p className="font-semibold">Error loading contacts:</p>
            <p>{contactsError}</p>
          </div>
        )}

        {!isLoadingContacts && !contactsError && contacts.length === 0 && (
          <p className="text-gray-600 text-center py-10">No contacts found. You might need to stage them first or check Xero.</p>
        )}

        {!isLoadingContacts && !contactsError && contacts.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.ContactID} onClick={() => handleContactClick(contact.ContactID)} className="hover:bg-gray-100 cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{contact.Name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.EmailAddress || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{contact.Phone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.IsCustomer && <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Customer</span>}
                        {contact.IsSupplier && <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 ${contact.IsCustomer ? 'ml-1' : ''}`}>Supplier</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="mt-6 flex items-center justify-between">
              <Button onClick={handlePreviousPage} disabled={currentPage <= 1 || isLoadingContacts} variant="secondary">
                <FaChevronLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-gray-700">Page {currentPage}</span>
              <Button onClick={handleNextPage} disabled={!nextPageDocId || isLoadingContacts} variant="secondary">
                Next
                <FaChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>
      
      <style jsx>{`
        .btn {
          padding: 0.6rem 1.2rem;
          font-weight: 600;
          border-radius: 0.375rem;
          transition: background-color 0.3s ease, opacity 0.3s ease;
          border: 1px solid transparent;
          cursor: pointer;
          font-size: 0.95rem;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-primary {
          background-color: #06B6D4; /* jims-cyan */
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background-color: #0E7490; /* jims-cyan-dark */
        }
        .btn-secondary {
          background-color: #E5E7EB; /* gray-200 */
          color: #1F2937; /* gray-800 */
          border: 1px solid #D1D5DB; /* gray-300 */
        }
        .btn-secondary:hover:not(:disabled) {
          background-color: #D1D5DB; /* gray-300 */
        }
      `}</style>
    </div>
  );
};

export default ImportXeroContactsPage;
